import {
  useGetOracleStepAnalyticEventInputs,
  useRegenerateWisdomResponse,
} from '@modules/oracle/hooks';
import { useOracleSearchStore } from '@modules/oracle/store/oracleSearchStore';
import { trackWisdomEvents } from '@modules/oracle/utils/trackWisdomEvents';
import { LoadingButton } from '@mui/lab';
import { Box, Tooltip } from '@mui/material';
import { useCallback, useRef } from 'react';

interface OracleResponseStepSubmitButtonProps {
  promptID: string;
  stepID: string;
  eql: string | undefined;
  disabled?: boolean;
  areInputsModified?: boolean;
}

export function OracleResponseStepSubmitButton({
  promptID,
  stepID,
  eql,
  disabled,
  areInputsModified,
}: OracleResponseStepSubmitButtonProps) {
  const isEndOfResponse = useOracleSearchStore(
    ({ data }) => data.promptByID?.[promptID].endOfResponse,
  );

  const stepAnalyticEventInputs = useGetOracleStepAnalyticEventInputs({
    promptID,
    stepID,
  });

  const dependencies = {
    promptID,
    eql,
    stepAnalyticEventInputs,
    stepID,
  };
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  const [regenerateResponse, { isLoading }] = useRegenerateWisdomResponse();

  const onSubmit = useCallback(async () => {
    await regenerateResponse({
      promptID: dependenciesRef.current.promptID,
      step: {
        stepID: dependenciesRef.current.stepID,
        eql: dependenciesRef.current.eql,
      },
      onRegenerate: () => {
        trackWisdomEvents({
          eventName: 'Wisdom - Step Query Edited',
          ...dependenciesRef.current.stepAnalyticEventInputs,
          stepPreviousEql:
            dependenciesRef.current.stepAnalyticEventInputs.stepEql,
          stepPrevEqlProps:
            dependenciesRef.current.stepAnalyticEventInputs.stepEqlProps,
          stepEql: dependenciesRef.current.eql,
          stepEqlProps: undefined,
        });
      },
    });
  }, [regenerateResponse]);

  // https://linear.app/enterpret/issue/ENG-18266/[stop-gap-solution]-stop-edits-till-memory-is-persisted
  // Due to a gap from BE side, we keep the Update CTA disabled
  // till the current response is streaming.
  // This is basically a FE workaround to hide the issue
  return (
    <Tooltip
      arrow
      placement="top"
      title={
        !isEndOfResponse && areInputsModified
          ? 'Edits are unavailable while the answer streams. Please wait until it finishes.'
          : ''
      }
    >
      <Box>
        <LoadingButton
          loading={isLoading}
          disabled={disabled || !isEndOfResponse}
          variant="contained"
          onClick={onSubmit}
        >
          Update
        </LoadingButton>
      </Box>
    </Tooltip>
  );
}
